import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getComicRow,
  submitAddCorrection,
  submitDeleteCorrection,
  submitEditCorrection,
} from "../../../services/readingOrdersApi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { debounce } from "lodash";
import { useToast } from "@chakra-ui/react";

export default function useSuggestionForm() {
  const { readingOrderId } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [action, setAction] = useState("add");
  const [order, setOrder] = useState("after");
  const [suggestedAdditions, setSuggestedAdditions] = useState([{}]);
  const [row, setRow] = useState("");
  const [debouncedRow, setDebouncedRow] = useState("");
  const [comic, setComic] = useState("");
  const [story, setStory] = useState("");

  const resetForm = () => {
    setAction("add");
    setOrder("after");
    setSuggestedAdditions([{}]);
    setRow("");
    setDebouncedRow("");
    setComic("");
    setStory("");
  };

  const onSuccess = () => {
    toast({
      title: "Suggestion submitted",
      description:
        "Thank you! Next step, the author will review your suggestion.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    queryClient.invalidateQueries(["readingOrderCorrections", readingOrderId]);
    resetForm();
  };

  const addMutation = useMutation(
    ({ comics }) => submitAddCorrection(readingOrderId, order, row, comics),
    {
      onSuccess: onSuccess,
    }
  );

  const editMutation = useMutation(
    (variables) =>
      submitEditCorrection(readingOrderId, row, comic.id, variables.story),
    {
      onSuccess: onSuccess,
    }
  );

  const deleteMutation = useMutation(
    () => submitDeleteCorrection(readingOrderId, row),
    {
      onSuccess: onSuccess,
    }
  );

  // Reset row when action changes
  useEffect(() => {
    setRow("");
    setComic("");
    setStory("");
  }, [action]);

  // Debounce the field to reload comic data
  const debouncedSetRow = useRef(
    debounce((newRow) => setDebouncedRow(newRow), 300)
  ).current;

  // Update debounced row
  useEffect(() => {
    debouncedSetRow(row);
    return () => {
      debouncedSetRow.cancel();
    };
  }, [row, debouncedSetRow]);

  // Comic row data
  const { data, isLoading } = useQuery(
    ["comicRow", debouncedRow],
    () => getComicRow(readingOrderId, debouncedRow),
    {
      enabled: !!debouncedRow,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setStory(data?.data?.story || "");
        if (action === "edit") {
          setComic(data?.data);
        } else {
          setComic("");
        }
      },
    }
  );

  const onSubmit = (e) => {
    e.preventDefault();
    // Sort out multiple entries
    const comics = suggestedAdditions.map((c) => ({
      comicId: c.id,
      story: (c.story || "").toUpperCase(),
    }));

    if (action === "add") {
      console.log("ADD correction");
      console.log(order, row, comics);
      addMutation.mutate({ comics });
    }

    if (action === "edit") {
      console.log("EDIT correction");
      console.log(action, row, comic.id, (story || "").toUpperCase());
      editMutation.mutate({ story: (story || "").toUpperCase() });
    }

    if (action === "delete") {
      console.log("DELETE correction");
      console.log(action, row);
      deleteMutation.mutate();
    }
  };

  return {
    action,
    setAction,
    row,
    setRow,
    comic,
    setComic,
    story,
    setStory,
    comicData: data?.data,
    isLoading,
    order,
    setOrder,
    onSubmit,
    suggestedAdditions,
    setSuggestedAdditions,
  };
}
